/**
 * My Top Movies service.
 * Owner routes need a signed-in user; the share card for an unsaved ranking,
 * the public share page and movie search work for guests.
 */
import { apiRequest, API_BASE_URL } from './api';
import { shareFooterQuery } from '@aluvo/constants/src/shareFooter';

export const TOP_MOVIES_ERROR_CODES = Object.freeze({
    NOT_FOUND: 'top_movies_not_found',
    INVALID_SIZE: 'invalid_top_movies_size',
    INCOMPLETE: 'top_movies_incomplete',
    DUPLICATE: 'duplicate_top_movie',
    INVALID_MOVIE: 'invalid_top_movie',
    LOOKUP_FAILED: 'top_movies_lookup_failed',
});

export function getTopMoviesErrorMessage(err, fallback = 'Something went wrong. Please try again.') {
    if (!err) return fallback;
    if (err.isNetworkError) return "You're offline. Your picks are saved on this device — try again when you're back online.";
    if (Object.values(TOP_MOVIES_ERROR_CODES).includes(err.code)) return err.message || fallback;
    if (err.statusCode === 429) return err.message || 'Too many requests. Please wait a moment and try again.';
    return err.message || fallback;
}

export const TopMoviesService = {
    /** → { topMovies: null | { id, size, ranked, visibility, shareSlug, shareUrl, cardUrl, movies } } */
    getMine() {
        return apiRequest('/top-movies/me');
    },

    /** Create or replace the whole ranking. movies = TMDB ids in display order. */
    save({ size, ranked, movies, visibility, ref }) {
        return apiRequest('/top-movies/me', {
            method: 'PUT',
            body: { size, ranked, movies, ...(visibility ? { visibility } : {}), ...(ref ? { ref } : {}) },
        });
    },

    setVisibility(visibility) {
        return apiRequest('/top-movies/me', { method: 'PATCH', body: { visibility } });
    },

    remove() {
        return apiRequest('/top-movies/me', { method: 'DELETE' });
    },

    /** Anyone-with-the-link view. → { isPrivate } or { list, owner, movies, shareUrl, cardUrl } */
    getShared(slug) {
        return apiRequest(`/share/top-movies/${encodeURIComponent(slug)}`, { authenticated: false });
    },

    /** Movies only (no TV) — the creator's search box. */
    searchMovies(query, page = 1) {
        return apiRequest('/media/search', { params: { q: query, page, kind: 'movie' }, authenticated: false });
    },

    /** PNG for a ranking that isn't saved/public yet: rendered from TMDB ids, no name on it. */
    draftCardUrl({ movies, size, ranked, format = 'square' }) {
        const ids = movies.map((movie) => movie.tmdbId).join(',');
        // The footer is in the URL so this publicly cached image is never one drawn with an older footer.
        return `${API_BASE_URL}/top-movies/card.png?ids=${ids}&size=${size}&ranked=${ranked ? 1 : 0}&format=${format}&${shareFooterQuery()}`;
    },

    /** PNG for a saved, public ranking (carries @username). No format → the 1200×630 link preview. */
    sharedCardUrl(slug, { format, owner = true } = {}) {
        const query = [format ? `format=${format}` : null, owner ? null : 'owner=0'].filter(Boolean).join('&');
        return `${API_BASE_URL}/share/top-movies/${encodeURIComponent(slug)}/card.png${query ? `?${query}` : ''}`;
    },

    /** Funnel events for the server log; never throws. */
    sendEvents(visitorId, events) {
        return apiRequest('/top-movies/events', { method: 'POST', body: { visitorId, events }, quiet: true, retry: { retries: 0 } })
            .catch(() => null);
    },
};
